import { APP_INITIALIZER, Provider } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import config from 'devextreme/core/config';
import { loadMessages, locale } from 'devextreme/localization';

const LANG_STORAGE_KEY = 'lang';
const RTL_LANGUAGES = ['ar'];
const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;

loadMessages({
  ar: {
    Yes: 'نعم',
    No: 'لا',
  },
});

export function applyLanguageDirection(lang: string): void {
  const isRtl = RTL_LANGUAGES.includes(lang);

  document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;

  config({ rtlEnabled: isRtl });
  locale(lang);
}

export function initializeLanguage(translate: TranslateService) {
  return async () => {
    const storedLang = localStorage.getItem(LANG_STORAGE_KEY);
    const defaultLang = translate.getDefaultLang() ?? 'en';
    const lang = SUPPORTED_LANGUAGES.includes(
      (storedLang ?? defaultLang) as (typeof SUPPORTED_LANGUAGES)[number],
    )
      ? (storedLang ?? defaultLang)
      : defaultLang;

    try {
      await firstValueFrom(translate.use(lang));
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      applyLanguageDirection(lang);
    } catch (error) {
      console.error('Failed to load language during app initialization.', error);
      localStorage.setItem(LANG_STORAGE_KEY, 'en');
      await firstValueFrom(translate.use('en'));
      applyLanguageDirection('en');
    }
  };
}

export const languageInitializerProvider: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initializeLanguage,
  deps: [TranslateService],
  multi: true,
};
